<template>
  <div
    data-component="base-settings-table"
    :class="{ 'is-disabled': isDisabled }"
  >
    <div class="table-header">
      <div
        v-for="column of columns"
        :key="column.key"
        class="table-header-column"
        :style="getColumnStyle(column)"
      >
        <div class="column-title">
          {{ column.label }}
        </div>
      </div>

      <!-- Add some padding to compensate for "Drag Start" button -->
      <div
        v-if="canReorder"
        class="table-header-placeholder"
      />
    </div>

    <div class="table-body">
      <!-- Virtual scroll doesn't' support re-ordering and has different props -->
      <base-virtual-scroll
        v-if="useVScroll"
        :data-key="dataKey"
        :items="items"
      >
        <template #default="{ index, data }">
          <div
            class="table-row"
            @click="$emit('item-click', { index, props: data, items})"
          >
            <slot
              name="item"
              :index="index"
              :props="data"
              :items="items"
            />
          </div>
        </template>
      </base-virtual-scroll>

      <base-sortable-list
        v-else
        :list="items"
        :is-reordable="canReorder"
        :is-disabled="isDisabled"
        handle="[data-drag]"
        @scroll="onScroll"
        @update="$emit('items-order-change', $event)"
      >
        <template #item="props">
          <div
            class="table-row"
            @click="onItemClick($event, props)"
          >
            <div class="table-row-content">
              <slot
                name="item"
                v-bind="props"
              />
            </div>

            <div
              v-if="canReorder"
              data-drag="drag-handle"
              :class="{ 'is-disabled': isDisabled }"
              @click.stop
            >
              <or-icon icon="drag_indicator" />
            </div>
          </div>
        </template>

        <template #footer="props">
          <slot
            name="table-footer"
            v-bind="props"
          />
        </template>
      </base-sortable-list>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { ListItem, OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import { BaseVirtualScrollV2 as BaseVirtualScroll } from '@/modules/virtual-scroll';
import BaseSortableList from '@/modules/common/components/BaseSortableList.vue';
import { BaseTableColumn } from '../types';

/**
 * "Can Reorder" option is exclusive with "useVScroll" option.
 * We should manually use Sortable.js so we can enable both features.
 */
export default defineComponent({
  name: 'BaseSettingsTable',
  components: { BaseVirtualScroll, BaseSortableList, OrIcon },
  props: {
    columns: {
      type: Array as PropType<BaseTableColumn[]>,
      default: () => [],
    },
    items: {
      type: Array as PropType<ListItem[]>,
      default: () => [],
    },
    dataKey: {
      type: String,
      default: 'key',
    },
    useVScroll: Boolean,
    canReorder: Boolean,
    isDisabled: Boolean,
  },
  emits: ['item-click', 'items-order-change', 'at-top', 'at-bottom'],
  methods: {
    onItemClick(event: MouseEvent, props: ListItem) {
      this.$emit('item-click', props);
    },

    getColumnStyle(column: BaseTableColumn) {
      return { ...column.style };
    },

    onScroll(event: MouseEvent) {
      const target = event.target as HTMLDivElement;
      if (target.scrollTop === 0) {
        this.$emit('at-top');
      }

      if (target.scrollHeight - target.scrollTop - target.clientHeight < 1) {
        this.$emit('at-bottom');
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-settings-table'] {
  --base-settings-table-drag-handle-size: var(--s-5);
  --base-settings-table-padding: var(--s-4);
  --base-settings-table-column-gap: var(--s-5);

  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  height: 100%;
  width: 100%;
  color: var(--or-c-on-background);

  &.is-disabled {
    [data-drag] {
      opacity: 0.65;
      pointer-events: none;
    }
  }

  .table-header {
    display: flex;
    flex: 0 0 auto;
    gap: var(--base-settings-table-column-gap);
    height: 3.25rem;
    padding: 0 var(--base-settings-table-padding);
    background: var(--or-c-surface-variant);
    border-radius: var(--s-2);
    box-shadow: var(--or-shadow);

    &-column {
      display: flex;
      flex: 1 1 100%;
      align-items: center;
      overflow: hidden;

      .column-title {
        @include text-overflow;
        font-size: var(--fs-1);
        font-weight: var(--fw-1);
      }
    }

    &-placeholder {
      flex: 0 0 var(--base-settings-table-drag-handle-size);
    }
  }

  .table-body {
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;

    ::v-deep .table-row {
      margin-bottom: var(--s-3);
      display: flex;
      align-items: center;
      gap: var(--base-settings-table-column-gap);
      padding: var(--base-settings-table-padding);
      background: var(--or-c-background);
      border-radius: var(--s-2);
      border: 1px solid var(--or-c-outline);
      cursor: pointer;

      &:hover {
        background: var(--or-c-primary-opacity-0-08);
      }

      &-content {
        flex: 1 1 100%;
        overflow: hidden;
      }

      [data-drag] {
        display: flex;
        margin-left: auto;
        color: var(--or-c-outline);
        user-select: none;
        cursor: grab;

        &:hover {
          color: var(--or-c-on-background);
        }

        .or-icon-v3 {
          color: inherit;
          height: var(--base-settings-table-drag-handle-size);
          width: var(--base-settings-table-drag-handle-size);
          font-size: var(--base-settings-table-drag-handle-size);
        }
      }
    }
  }
}
</style>
